/** Shared RTK Query API transport backed by the canonical Axios client. */
import { createApi, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import type { AxiosRequestConfig, Method } from 'axios';

import { serializeApiError, SerializedApiError } from '../../services/apiError';
import axiosInstance from '../../services/axiosInstance';

type QueryArgument = string | {
  url: string;
  method?: Method;
  body?: unknown;
  data?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
};

const axiosBaseQuery: BaseQueryFn<QueryArgument, unknown, SerializedApiError> = async (
  argument,
  queryApi,
) => {
  try {
    const config: AxiosRequestConfig = typeof argument === 'string'
      ? { url: argument, method: 'GET' }
      : {
        url: argument.url,
        method: argument.method || 'GET',
        data: argument.body ?? argument.data,
        params: argument.params,
        headers: argument.headers,
      };

    config.signal = queryApi.signal;
    const response = await axiosInstance.request(config);

    return { data: response.data };
  } catch (error) {
    return { error: serializeApiError(error) };
  }
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery: axiosBaseQuery,
  // Planning resources already reconcile through SSE/tag invalidation. A
  // blanket focus refresh caused every mounted artifact to fetch together and
  // several panels to replace good cached content with loaders on app switch.
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: [
    'DesignPage',
    'Requirements',
    'RequirementsProposal',
    'CreativeDirection',
    'ScopeReading',
    'ArtifactVersion',
    'ChatSession',
    'UserFlow',
    'UserFlowVersion',
    'UserFlowProposal',
    'Architecture',
    'ArchitectureProposal',
    'PlanningStatus',
    'PlanningStartQuote',
    'AgentLogs',
    'BuildPhases',
    'PhaseBuildState',
    'Task',
    'TaskVersion',
    'GanttSchedule',
    'Reward',
  ],
  endpoints: () => ({}),
});
